import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  content: '',
  cursorPosition: null,
  manualCursor: false,
  undoStack: [],
  redoStack: [],
};

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (text) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Helper function to save current state to undo stack
const saveToUndoStack = (state) => {
  // Only keep up to 3 previous states
  const newUndoStack = [...state.undoStack];
  if (newUndoStack.length >= 3) {
    newUndoStack.shift(); // Remove oldest state
  }
  newUndoStack.push({
    content: state.content,
    cursorPosition: state.cursorPosition,
  });
  state.undoStack = newUndoStack;
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setContent: (state, action) => {
      if (state.content !== action.payload) {
        saveToUndoStack(state);
        state.content = action.payload;
        state.redoStack = []; // Clear redo stack on new content
      }
    },
    setCursorPosition: (state, action) => {
      state.cursorPosition = action.payload;
      state.manualCursor = true;
    },
    replaceSelectedText: (state, action) => {
      const { text, selectionStart, selectionEnd } = action.payload;
      saveToUndoStack(state);
      state.redoStack = []; // Clear redo stack on new content
      
      const beforeSelection = state.content.slice(0, selectionStart);
      const afterSelection = state.content.slice(selectionEnd);
      
      // Check if we need to capitalize based on context
      const lastCharBeforeTrimmed = beforeSelection.replace(/[ \t\n]+$/, '').slice(-1);
      const shouldCapitalize = /[.:]/.test(lastCharBeforeTrimmed) || lastCharBeforeTrimmed === '[';
      const modifiedText = shouldCapitalize ? capitalizeFirstLetter(text) : text;
      
      state.content = beforeSelection + modifiedText + afterSelection;
      state.cursorPosition = selectionStart + modifiedText.length;
      state.manualCursor = true;
    },
    appendTextToPlaceholder: (state, action) => {
      const { text, cursorPosition } = action.payload;
      
      // Save current state before modifying
      saveToUndoStack(state);
      state.redoStack = []; // Clear redo stack on new content
      
      const beforeCursor = state.content.slice(0, cursorPosition);
      const afterCursor = state.content.slice(cursorPosition);
    
      const trimmedBeforeCursor = beforeCursor.replace(/[ \t\n]+$/, '');
      const lastCharBeforeTrimmed = trimmedBeforeCursor.slice(-1);
      const isColonPlaceholder = beforeCursor.slice(-2) === ':[';
      const isPlaceholder = state.content.slice(cursorPosition - 1, cursorPosition + 1) === '[]';
      const shouldCapitalize =
        /[.:]/.test(lastCharBeforeTrimmed) || 
        lastCharBeforeTrimmed === '[' ||
        lastCharBeforeTrimmed === '\n' ||  // Capitalize after newline
        beforeCursor.endsWith('\n');  // Also check if we're right after a newline
      const modifiedText = shouldCapitalize ? capitalizeFirstLetter(text) : text;
      
      // Don't add space before if the text is a period, after newline, or if it meets other conditions
      const needsSpaceBefore =
        beforeCursor.length > 0 &&
        beforeCursor.slice(-1) !== ' ' &&
        beforeCursor.slice(-1) !== '[' &&
        beforeCursor.slice(-1) !== '\n' &&
        text !== '.';
      const prefix = needsSpaceBefore ? ' ' : '';
      const needsSpaceAfter = afterCursor.charAt(0) !== '\n';
    
      if (isColonPlaceholder) {
        state.content =
          beforeCursor.slice(0, -1) + prefix + modifiedText + afterCursor.slice(1);
        state.cursorPosition = cursorPosition - 1 + prefix.length + modifiedText.length;
      } else if (isPlaceholder) {
        state.content =
          beforeCursor.slice(0, -1) + prefix + modifiedText + afterCursor.slice(1);
        state.cursorPosition = cursorPosition - 1 + prefix.length + modifiedText.length;
      } else {
        state.content =
          beforeCursor + prefix + modifiedText + (needsSpaceAfter ? ' ' : '') + afterCursor;
        state.cursorPosition =
          cursorPosition + prefix.length + modifiedText.length + (needsSpaceAfter ? 1 : 0);
      }
    
      state.manualCursor = false;
    },
    
    moveToNextPlaceholder: (state) => {
      const placeholderPattern = /\[\]/g;
      const currentCursorPosition = state.cursorPosition || 0;

      const matches = [...state.content.matchAll(placeholderPattern)];
      const nextPlaceholder = matches.find(
        (match) => match.index > currentCursorPosition
      );

      if (nextPlaceholder) {
        state.cursorPosition = nextPlaceholder.index + 1;
        state.manualCursor = false;
      } else {
        state.cursorPosition = null;
      }
    },
    placeCursor: (state, action) => {
      state.manualCursor = action.payload;
    },
    undo: (state) => {
      if (state.undoStack.length > 0) {
        const lastState = state.undoStack.pop();
        if (lastState) {
          // Save current state to redo stack before undoing
          state.redoStack.push({
            content: state.content,
            cursorPosition: state.cursorPosition
          });
          state.content = lastState.content;
          state.cursorPosition = lastState.cursorPosition;
          state.manualCursor = true;
        }
      }
    },
    redo: (state) => {
      if (state.redoStack.length > 0) {
        const nextState = state.redoStack.pop();
        if (nextState) {
          // Save current state to undo stack before redoing
          state.undoStack.push({
            content: state.content,
            cursorPosition: state.cursorPosition
          });
          state.content = nextState.content;
          state.cursorPosition = nextState.cursorPosition;
          state.manualCursor = true;
        }
      }
    },
  },
});

export const {
  setContent,
  setCursorPosition,
  appendTextToPlaceholder,
  moveToNextPlaceholder,
  placeCursor,
  undo,
  redo,
  replaceSelectedText,
} = editorSlice.actions;

export default editorSlice.reducer; 